import { ArrowUp } from "lucide-react";
import { useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipShortcut,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { getErrorMessage } from "@/core/api/errors";
import { formatShortcutAriaLabel } from "@/core/utils/desktop-platform";

const MAX_MESSAGE_LENGTH = 5000;
const COUNTER_VISIBLE_FROM = MAX_MESSAGE_LENGTH - 500;
const COMPOSER_MAX_HEIGHT_PX = 148;
const SEND_SHORTCUT = ["mod", "Enter"] as const;

const fitToContent = (field: HTMLTextAreaElement | null) => {
  if (!field) return;
  field.style.height = "auto";
  field.style.height = `${Math.min(field.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
};

/** A compact reply field that grows until it reaches six lines. */
export const PackChatReplyForm = ({
  loading,
  reopens,
  placeholder,
  actionLabel,
  autoFocus,
  onReply,
}: {
  loading?: boolean;
  reopens?: boolean;
  placeholder?: string;
  actionLabel?: string;
  autoFocus?: boolean;
  onReply: (body: string) => Promise<void>;
}) => {
  const [body, setBody] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);
  const trimmed = body.trim();

  const send = async () => {
    if (!trimmed || loading) return;
    try {
      await onReply(trimmed);
      setBody("");
      if (field.current) field.current.style.height = "auto";
    } catch (error) {
      toast.error("Message not sent", { description: getErrorMessage(error) });
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void send();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    void send();
  };

  const action = actionLabel ?? (reopens ? "Reply and reopen" : "Send reply");
  const sendShortcutAriaLabel = formatShortcutAriaLabel(SEND_SHORTCUT);

  return (
    <form onSubmit={submit} className="w-full">
      <Stack fullWidth gap="xs" align="stretch">
        <Stack
          direction="row"
          align="end"
          gap="sm"
          fullWidth
          className="rounded-lg border border-input px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-ring"
        >
          <textarea
            ref={field}
            rows={1}
            aria-label="Reply message"
            value={body}
            maxLength={MAX_MESSAGE_LENGTH}
            autoFocus={autoFocus}
            placeholder={
              placeholder ??
              (reopens ? "Reply to reopen this thread" : "Write a reply")
            }
            disabled={loading}
            className="min-w-0 flex-1 resize-none bg-transparent py-1 text-md leading-6 outline-none placeholder:text-tertiary-text disabled:opacity-60"
            onKeyDown={onKeyDown}
            onChange={(event) => {
              setBody(event.target.value);
              fitToContent(event.target);
            }}
          />
          <Tooltip>
            <TooltipTrigger asDiv className="inline-flex shrink-0">
              <button
                type="submit"
                aria-label={`${action} (${sendShortcutAriaLabel})`}
                disabled={!trimmed || loading}
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-[#009973] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:bg-bg-disabled disabled:text-tertiary-text"
              >
                <ArrowUp className="size-4" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <TooltipShortcut keys={SEND_SHORTCUT}>{action}</TooltipShortcut>
            </TooltipContent>
          </Tooltip>
        </Stack>
        {body.length >= COUNTER_VISIBLE_FROM && (
          <Typography
            variant="bodyW3"
            textColor="tertiary"
            className="text-[12px]"
          >
            {MAX_MESSAGE_LENGTH - body.length} characters left
          </Typography>
        )}
      </Stack>
    </form>
  );
};
